import React from "react";
import type { PreviousRequestSummary } from "@/api";
import { Button, StatusPill } from "@/ds";
import { fmtStamp } from "@/lib";
import { useT } from "../../i18n";
import styles from "./previous-requests.module.css";

export function PreviousRequests({
  items,
  loading,
  error,
  nextCursor,
  selectedRequestId,
  selectionLoadingId,
  disabled,
  onOpen,
  onRetry,
  onLoadMore,
  onSelect,
}: {
  items: PreviousRequestSummary[] | null;
  loading: boolean;
  error: string | null;
  nextCursor: string | null;
  selectedRequestId: string | null;
  selectionLoadingId: string | null;
  disabled: boolean;
  onOpen: () => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onSelect: (requestId: string) => void;
}) {
  const t = useT();
  const opened = React.useRef(false);

  React.useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    onOpen();
  }, [onOpen]);

  return (
    <div
      className={styles.root}
      data-testid="previous-requests"
    >
      <div className={styles.body}>
        {loading && items === null && <p className={styles.message}>{t("common.loading")}</p>}
        {error && (
          <div className={styles.error} role="alert">
            <span>{t("ws.previous.loadError")}</span>
            <button type="button" className="linkbtn" onClick={items ? onLoadMore : onRetry}>
              {t("common.retry")}
            </button>
          </div>
        )}
        {!loading && !error && items?.length === 0 && (
          <p className={styles.message}>{t("ws.previous.empty")}</p>
        )}
        {items && items.length > 0 && (
          <>
            <ul className={styles.list}>
              {items.map((item) => {
                const selected = item.id === selectedRequestId;
                const busy = item.id === selectionLoadingId;
                const stamp = item.latest_message_received_at ?? item.created_at;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={styles.item}
                      data-previous-request-id={item.id}
                      aria-current={selected ? "true" : undefined}
                      aria-label={t("ws.previous.openAria", { n: item.request_no })}
                      disabled={disabled || selectionLoadingId !== null}
                      onClick={() => onSelect(item.id)}
                    >
                      <span className={styles.itemText}>
                        <span className={styles.itemTop}>
                          <strong>#{item.request_no}</strong>
                          <time>{fmtStamp(stamp)}</time>
                        </span>
                        <span className={styles.subject}>{item.subject || t("common.noSubject")}</span>
                      </span>
                      <span className={styles.badges}>
                        <StatusPill
                          size="sm"
                          tone="success"
                          variant="soft"
                          label={t(`status.lifecycle.${item.lifecycle_status}`)}
                        />
                        {item.archived_at && (
                          <StatusPill size="sm" tone="neutral" label={t("ws.previous.archived")} />
                        )}
                        {busy && <span className={styles.busy}>{t("common.loading")}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {nextCursor && (
              <Button
                type="button"
                variant="ghost"
                className={styles.loadMore}
                loading={loading}
                disabled={loading}
                onClick={onLoadMore}
              >
                {t("ws.previous.loadMore")}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
